// artifacts/edison/src/components/tour/tour-steps.ts

export interface TourStep {
  id: string;
  target: string | null; // data-tour value, null = no target (centred)
  title: string;
  body: string;
  /** 'setup' steps show a navigation CTA + "I've done this" skip */
  type: 'normal' | 'setup';
  /** wouter path to navigate to when user clicks the setup CTA */
  ctaPath?: string;
  ctaLabel?: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    target: 'dashboard-stats',
    title: 'Welcome to Edison',
    body: "Edison captures your R&D evidence continuously so you're always ready to lodge your R&DTI claim. Let's walk through the key workflow.",
    type: 'normal',
  },
  {
    id: 'profile',
    target: 'profile-paste',
    title: 'Complete your profile',
    body: "Paste your LinkedIn profile or CV here. Edison extracts your qualifications and R&D expertise — this becomes your competence record for AusIndustry, proving you're qualified to carry out the R&D work.",
    type: 'setup',
    ctaPath: '/profile',
    ctaLabel: 'Go to Profile',
  },
  {
    id: 'device',
    target: 'devices-section',
    title: 'Register your development machine',
    body: "Register the machine you code on so your Claude Code sessions are captured as R&D evidence automatically. You'll run a one-line setup script — takes 30 seconds.",
    type: 'setup',
    ctaPath: '/integrations',
    ctaLabel: 'Go to Integrations',
  },
  {
    id: 'projects',
    target: 'projects-nav',
    title: 'Create a project per financial year',
    body: 'Each R&D project tracks one body of work for a financial year. Connect your GitHub repo and evidence flows in automatically.',
    type: 'normal',
    ctaPath: '/projects',
    ctaLabel: 'Go to Projects',
  },
  {
    id: 'tfa',
    target: 'tfa-panel',
    title: 'Define your technical uncertainty',
    body: "The Technical Feasibility Assessment (TFA) is AusIndustry's core eligibility test. A panel documents the genuine technical unknowns your team had to solve.",
    type: 'normal',
  },
  {
    id: 'tfa-lock',
    target: 'lock-tfa',
    title: 'Lock the TFA — unlock everything else',
    body: 'Once locked, activities and AI classification become available. Locking is permanent — it signals the panel reached a formal determination.',
    type: 'normal',
  },
  {
    id: 'activities',
    target: 'activities-tab',
    title: 'Describe your R&D work streams',
    body: 'Activities break your project into discrete streams of R&D work. Each records IP ownership and the competence of the people involved.',
    type: 'normal',
  },
  {
    id: 'timewriting',
    target: 'confidence-score',
    title: 'Classify time as R&D or not',
    body: 'AI reads your commit history against the TFA scope and assigns each time block an R&D percentage. High-confidence entries (>85%) can be bulk approved.',
    type: 'normal',
    ctaPath: '/timewriting',
    ctaLabel: 'Go to Time',
  },
  {
    id: 'claims',
    target: 'claims-nav',
    title: 'Assemble your lodgement',
    body: 'A Claim pulls together all approved time, activities, and costs for a financial year. When the checklist is green, lock it and hand it to your accountant.',
    type: 'normal',
    ctaPath: '/claims',
    ctaLabel: 'Go to Claims',
  },
  {
    id: 'audit-readiness',
    target: 'audit-readiness-nav',
    title: 'Check your audit readiness',
    body: 'The Audit Readiness page gives you a per-project eligibility report — TFA status, core activities, and what gaps remain before you can lock your claim.',
    type: 'normal',
    ctaPath: '/audit-readiness',
    ctaLabel: 'Go to Audit Readiness',
  },
  {
    id: 'assistant',
    target: 'edison-assistant',
    title: 'Ask Edison anything',
    body: "The Edison Assistant answers questions about TFA, time classification, and R&D compliance. Press ⌘K from anywhere to search or ask — everything is ≤ 2 keystrokes away.",
    type: 'normal',
  },
  {
    id: 'done',
    target: null,
    title: "You're set up and ready",
    body: "That's everything. Re-open this tour anytime from the ? button. Your first step: complete your profile, then register your machine.",
    type: 'setup',
    ctaPath: '/profile',
    ctaLabel: 'Complete profile',
  },
];
